import CustomButton from '@/components/atoms/CustomButton';
import { getContentTypeLabel } from '@/modules/content/common/contentDisplay';
import { TContentType } from '@/types/Content.types';
import { HStack, Text, VStack } from '@chakra-ui/react';
import React, { useState } from 'react';
import { hasDetailsError, validateContentStep, validateDetails, validateForSubmit } from '../brandedContentValidation';
import {
  TBrandedFormErrors,
  TBrandedFormMode,
  TBrandedFormSubmit,
  TBrandedFormValues,
  WizardFinalAction,
} from '../types';
import { useBrandedContentForm } from '../useBrandedContentForm';
import ArticleSectionsEditor from './ArticleSectionsEditor';
import ContentPreviewStep from './ContentPreviewStep';
import DetailsStep from './DetailsStep';
import VideoDescriptionStep from './VideoDescriptionStep';

export type { TBrandedFormMode } from '../types';

// Articles get an extra read-only "arrange" step (manage images / sections)
// between editing and preview; post/video go straight to preview.
type WizardStep = 'details' | 'content' | 'arrange' | 'preview';

// The content step is labelled with the content type itself (Post / Article / Reel).
const stepTitle = (step: WizardStep, type: TContentType): string => {
  switch (step) {
    case 'details':
      return 'Add Thumbnail and Details';
    case 'content':
      return getContentTypeLabel(type);
    case 'arrange':
      return 'Arrange';
    case 'preview':
      return 'Content Preview';
  }
};

type Props = {
  type: TContentType;
  defaultValues?: Partial<TBrandedFormValues>;
  defaultThumbnailUrl?: string | null;
  isSubmitting: boolean;
  // "Go Back" from the first step (e.g. back to the list).
  onExit: () => void;
  onSubmit: (mode: TBrandedFormMode, data: TBrandedFormSubmit) => void;
  // Terminal action on the Preview step (e.g. "Publish Content" / "Save changes").
  primaryAction: WizardFinalAction;
  // Optional action on the Content step to save without previewing (e.g. "Save draft").
  secondaryAction?: WizardFinalAction;
};

const BrandedContentWizard = ({
  type,
  defaultValues,
  defaultThumbnailUrl,
  isSubmitting,
  onExit,
  onSubmit,
  primaryAction,
  secondaryAction,
}: Props) => {
  const [step, setStep] = useState<WizardStep>('details');
  const {
    values,
    errors,
    setErrors,
    setField,
    setSections,
    setDomains,
    setSubdomains,
    thumbnailFile,
    setThumbnailFile,
  } = useBrandedContentForm(defaultValues);

  const goToContent = () => {
    const nextErrors = validateDetails(values, type);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) setStep(hasContentStep ? 'content' : 'preview');
  };

  // An error is only visible on the step that renders its field. Details errors belong to
  // step 1; body, section text and full description are all editable on the content step
  // (the arrange step shows section text read-only, so it cannot fix a length breach).
  // Anything else has no step that can display it — surfaced inline on preview instead,
  // so submit is never blocked silently.
  const stepForErrors = (nextErrors: TBrandedFormErrors): WizardStep | undefined => {
    if (hasDetailsError(nextErrors)) return 'details';
    if (nextErrors.sections || nextErrors.fullDescription) return 'content';
    return undefined;
  };

  const submit = (mode: TBrandedFormMode) => {
    const nextErrors = validateForSubmit(values, type, mode);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      const targetStep = stepForErrors(nextErrors);
      if (targetStep) setStep(targetStep);
      return;
    }
    onSubmit(mode, { values, thumbnailFile });
  };

  const goToNextStepFromContent = () => {
    const nextErrors = validateContentStep(values, type);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) setStep(afterContentStep);
  };

  // Errors with no editable step (e.g. a stored value a tightened limit now rejects).
  const unroutableError = Object.keys(errors).length > 0 && !stepForErrors(errors) ? Object.values(errors)[0] : undefined;

  const actionButton = (action: WizardFinalAction) => (
    <CustomButton
      key={action.key}
      label={action.label}
      backgroundColor={action.backgroundColor}
      isTransparent={action.isTransparent}
      labelColor={action.isTransparent ? 'text.mediumBlue' : 'extra.white'}
      labelHoverColor={action.isTransparent ? 'extra.black' : undefined}
      isLoading={isSubmitting}
      onClick={() => submit(action.mode)}
    />
  );

  const continueButton = (onClick: () => void) => (
    <CustomButton label="Continue" backgroundColor="extra.orange" onClick={onClick} />
  );

  const goBackButton = (onClick: () => void) => (
    <CustomButton
      label="Go Back"
      isTransparent
      labelColor="background.blueGray"
      labelHoverColor="extra.black"
      onClick={onClick}
    />
  );

  const contentStepByType: Partial<Record<TContentType, React.ReactNode>> = {
    article: (
      <ArticleSectionsEditor mode="edit" sections={values.sections} onChange={setSections} error={errors.sections} />
    ),
    video: (
      <VideoDescriptionStep
        fullDescription={values.fullDescription}
        onChange={(value) => setField('fullDescription', value)}
        error={errors.fullDescription}
      />
    ),
  };

  // After editing, articles go to the read-only "arrange" step; others to preview.
  // A post is a single block of text written as the description on the details step, so it
  // has no content step of its own; articles and videos still do.
  const hasContentStep = type !== 'post';
  const afterContentStep: WizardStep = type === 'article' ? 'arrange' : 'preview';
  // The step immediately before preview — where "Go Back" from preview returns to.
  const beforePreviewStep: WizardStep = type === 'article' ? 'arrange' : hasContentStep ? 'content' : 'details';

  return (
    <VStack align="center" width="100%" gap="24px" marginBottom="48px">
      {/* Top bar: save-as-draft on the right, available on every step. */}
      {secondaryAction && (
        <HStack width="100%" justify="flex-end">
          {actionButton(secondaryAction)}
        </HStack>
      )}

      <Text fontWeight={500} fontSize="30px" color="text.mediumBlue" fontFamily="Lora" fontStyle="italic">
        {stepTitle(step, type)}
      </Text>

      {step === 'details' && (
        <>
          <DetailsStep
            type={type}
            values={values}
            errors={errors}
            thumbnailFile={thumbnailFile}
            defaultThumbnailUrl={defaultThumbnailUrl}
            onField={setField}
            onDomainsChange={setDomains}
            onSubdomainsChange={setSubdomains}
            onThumbnailChange={setThumbnailFile}
          />
          <VStack gap="10px">
            {continueButton(goToContent)}
            {goBackButton(onExit)}
          </VStack>
        </>
      )}

      {step === 'content' && (
        <>
          {contentStepByType[type]}
          <VStack gap="10px">
            {continueButton(goToNextStepFromContent)}
            {goBackButton(() => setStep('details'))}
          </VStack>
        </>
      )}

      {step === 'arrange' && (
        <>
          <ArticleSectionsEditor
            mode="arrange"
            sections={values.sections}
            onChange={setSections}
            error={errors.sections}
          />
          <VStack gap="10px">
            {continueButton(() => setStep('preview'))}
            {goBackButton(() => setStep('content'))}
          </VStack>
        </>
      )}

      {step === 'preview' && (
        <>
          <ContentPreviewStep
            type={type}
            values={values}
            thumbnailFile={thumbnailFile}
            defaultThumbnailUrl={defaultThumbnailUrl}
          />
          {unroutableError && (
            <Text color="extra.red" fontSize="13px" textAlign="center">
              {unroutableError}
            </Text>
          )}
          <VStack gap="10px">
            {actionButton(primaryAction)}
            {goBackButton(() => setStep(beforePreviewStep))}
          </VStack>
        </>
      )}
    </VStack>
  );
};

export default BrandedContentWizard;
